import React, { RefObject } from 'react';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';
import { MemoryHistory } from 'history';

import useTracking from 'hooks/analytics';

import List from 'components/common/List';
import CircleTile from 'components/common/tile/CircleTile';

import { pageSelected } from 'actions/pageView';

import { getContentRowMetadata } from 'support/analytics/analyticsHelper';

import { KitchenSinkRowItem } from 'store-types';
import { PageType } from 'app-types';

interface TrackContentEventParams {
  rowTitle?: string | null;
  rowPos?: number | null;
  rowCol: number | null;
  tag?: string | null;
  pageType?: PageType | null;
}

const trackContentEvent = (
  trackEvent: ((eventName: string, eventData: Record<string, unknown>) => unknown) | undefined,
  { rowTitle, rowPos, rowCol, tag, pageType }: TrackContentEventParams,
) => {
  const eventData = {
    ...getContentRowMetadata(rowTitle, rowPos, rowCol),
    moduleAction: 'click',
    clickText: `${pageType} carousel - ${tag}`,
  };
  trackEvent?.(`${pageType} carousel interactions`, eventData);
};

interface CircleRowItem {
  id?: string | number;
  tag?: {
    key?: string;
    type?: {
      name?: PageType;
    };
  };
  displayName?: string;
  staticImageUrl?: string;
  staticActiveImageUrl?: string;
  lottieImageUrl?: string;
  [key: string]: unknown;
}

interface CircleRowProps {
  displayTitle: boolean;
  title?: string | null;
  row: KitchenSinkRowItem;
  className?: string | null;
  rowClassName?: string | null;
  titleClassName?: string | null;
  forwardedRef?: React.Ref<HTMLElement> | null;
  focusIdPrefix?: string | null;
  rowPos?: number | null;
  onEndOfNavigationOverride?: ((index: number) => void) | null;
}

const CircleRow = ({
  displayTitle,
  title = null,
  row,
  rowClassName = null,
  titleClassName = null,
  forwardedRef = null,
  rowPos = null,
  onEndOfNavigationOverride,
}: CircleRowProps) => {
  const history = useHistory() as MemoryHistory;
  const dispatch = useDispatch();
  const { trackEvent } = useTracking();

  const renderItem = (item: CircleRowItem, index: number, ref?: RefObject<HTMLDivElement>) => {
    const tag = item?.tag?.key;
    const pageType = item?.tag?.type?.name;
    const onClick = () => {
      dispatch(pageSelected(tag, pageType));
      trackContentEvent(trackEvent, {
        rowTitle: title,
        rowPos,
        rowCol: index + 1,
        tag,
        pageType,
      });
      history.push({
        pathname: `/${pageType}/${tag}`,
      });
    };

    return (
      <CircleTile
        title={item?.displayName}
        focusId={`row-${row.slug}-item-${item?.id}`}
        item={item}
        row={row}
        ref={ref}
        key={item?.id}
        staticImageSrc={item?.staticImageUrl}
        staticActiveImage={item?.staticActiveImageUrl}
        lottieImageSrc={item?.lottieImageUrl}
        index={index}
        onClick={onClick}
      />
    );
  };

  return (
    <>
      {displayTitle && (
        <div className={classNames(titleClassName, 'font-weight-bold')}>
          {title}
        </div>
      )}
      <List
        focusId={row.slug}
        data={row.data}
        buffer={6}
        className={rowClassName}
        renderItem={renderItem}
        peekRatio={0}
        restorationId={`${row.slug}`}
        ref={forwardedRef}
        onEndOfNavigationOverride={onEndOfNavigationOverride}
      />
    </>
  );
};

export default React.memo(
  React.forwardRef<HTMLElement, Omit<CircleRowProps, 'forwardedRef'>>((props, ref) => <CircleRow {...props} forwardedRef={ref} />),
);
